/**
 * Spec №13 Stage C + №13.1 Stage 2 — Matter Cockpit **v2** (the hub, `place/matter/:id`).
 *
 * The four-plane record (D-W1) + the two №13.1 additions (design-pack §2.1):
 *   • the **record-action bar** renders `list_context_actions("matter", id)` — every
 *     launch prefilled + LOCKED; commercial-spine actions appear only when installed;
 *   • the **tabbed record** — Overview · Billing · Trust · Register · Conflicts · Files —
 *     whose Billing tab presents fee arrangements as cards with the invariant picker.
 *
 * Cross-entity launches all go through №11.1 contextual actions (zero hand-wired
 * prefill). A closed matter is governed: the Edit affordance becomes the lock
 * teaching UI + a Reopen action (§1.5). Balances are never stored (G2/L-2): WIP is
 * derived, AR is honestly empty until Xero (12.x), Trust is ledger-live.
 *
 * SUPERSEDED IN PART — №53.3.1 S0 (D-CR24/D-CR26), 2026-08-20. The tab set above
 * is stale on two counts, corrected here rather than rewritten in place (the
 * record of what we thought at the time has value): "Overview" is "Stream"
 * (№53.3 gave the landing view a stream instead of a field grid), and a
 * "Details" tab now closes the set (E-33 — the record's declared sections,
 * evicted here from the landing view). The header is no longer portalled into
 * the shell's stage head — it renders as `.rechead` on the page itself.
 */
import { useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router";
import type { EntityRecord } from "../../../lib/types";
import { CreateEntityModal } from "./CreateEntityModal";
import { ContextActionBar } from "./ContextActionBar";
import { MatterTrustSlice } from "./MatterTrustSlice";
import { governanceFor } from "./governance-map";
import { mcpClient } from "../../../lib/mcp-client";
import { showToast } from "../../components/primitives";
import { useEntityType } from "../../../lib/hooks/use-kuvio";
import { useRowDetail } from "../../hooks/use-row-detail";
import { usePlaceViews } from "../../hooks/use-place-views";
import { useListFiles } from "../../hooks/use-list-files";
import {
  useMatterWip, useMatterTrust,
  useFeeArrangements, useRegisterEntries, useConflictChecks,
  useMatterParties, useSetPrimaryClient, useRemoveMatterParty,
  useEffectiveRates, useMatterTeam,
} from "../../hooks/use-time-billing";
import { formatRelativeTime } from "../../lib/v3-columns";
import { money } from "../../components/ledger/format";
import { columnsByName, fieldDisplay, sentenceLabel } from "../../lib/declared-view";
import { mostUrgentKeyDate } from "../../lib/key-dates";
import { RecordView } from "./RecordView";
import { conflictResultDisplay } from "./verb-forms";
import { RestrictMatterModal } from "../../components/scope/RestrictMatterModal";
import { useMatterRestriction } from "../../hooks/use-policy";
import { DecisionCard } from "../../components/collab/DecisionCard";
import { useAnswerNeedsYou, useSkipNeedsYou, usePlayTaskRun } from "../../hooks/use-task-thread";
import { useJustAnswered, receiptFor, type AnsweredReceipt } from "../../hooks/use-just-answered";
import { useCoPanel } from "../../components/collab/co-panel-context";
import { AnsweredStrip } from "../../components/collab/AnsweredStrip";
import { MatterStream } from "./MatterStream";
import { surfaceRows, relatedCountsByRowKey } from "./matter-stream-model";
import { useAnchorStream } from "../../hooks/use-anchor-stream";
import { useSubViewCounts } from "./stage-head";
import { resolveSubject } from "./route-anchor-matrix";

/**
 * №29 (S4, §3 ④) — the cockpit's views, which were in-page tabs until S4.
 *
 * They are ROUTES now, so the left nav's OPEN sub-nav can carry them: a sub-nav row can
 * only point at something addressable, and `useState` is not an address. The union below
 * is unchanged — what changed is where the current one comes from (the matrix row for the
 * pathname) and where a click goes (a navigation, not a setState). Deep links and Back
 * work as a consequence, not as a feature.
 */
type Tab = "overview" | "parties" | "billing" | "trust" | "register" | "conflicts" | "files" | "details";

/** matrix row key → the view it opens. The record root is the overview
 *  (Stream, since 53.3). `details` (E-33, 53.3.1 S0) is the record's declared
 *  sections' new home. */
const TAB_FOR_ROW: Readonly<Record<string, Tab>> = {
  "place-record": "overview",
  "place-record-parties": "parties",
  "place-record-billing": "billing",
  "place-record-trust": "trust",
  "place-record-register": "register",
  "place-record-conflicts": "conflicts",
  "place-record-files": "files",
  "place-record-details": "details",
};
const methodBadge = (m: unknown) => (m === "fixed" ? { cls: "badge--fixed", label: "Fixed" } : { cls: "badge--tm", label: "T&M" });

export function MatterCockpitPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const type = useEntityType("matter");
  const detail = useRowDetail("matter", id);
  const views = usePlaceViews("matter");

  const wip = useMatterWip(id);
  const trust = useMatterTrust(id);
  const arrangements = useFeeArrangements(id);
  const registers = useRegisterEntries(id);
  const conflicts = useConflictChecks(id);
  const parties = useMatterParties(id);
  const setPrimary = useSetPrimaryClient();
  const removeParty = useRemoveMatterParty();
  // №23 Stage C3 (D-MS3 rev/D-MS4 rev/D-MS9) — Effective rates + Team, the two
  // remaining declared sections on the matter stage (Key Dates shipped C2).
  const effectiveRates = useEffectiveRates(id);
  const team = useMatterTeam(id);
  // №53 · 53.3 — the matter's own stream. The `record` rollup already existed
  // (D-CV12); this rung gave it the matter's entity-level activities, its recorded
  // time and its artefact chips. Not `live`: a record page is read, and a 2.5s poll
  // behind a partner's reading is the panel's cadence, not a page's.
  const stream = useAnchorStream(id ? { kind: "record", id } : null, { live: false, limit: 120 });
  const files = useListFiles(id);
  const answer = useAnswerNeedsYou();
  // №22.5 (C4) — the matter rail is the fourth home of the ONE card; it skips too.
  const skip = useSkipNeedsYou();
  const play = usePlayTaskRun(); // №22.5 (F10)
  const ja = useJustAnswered(); // №22.2 (D-CV1) — resolve in place, don't vanish
  const coPanel = useCoPanel(); // №22.3 (D-CH4) — view thread → the docked panel
  // №29 (S4, §3 ④) — the open view is the ROUTE's, read through the same matrix the nav
  // and the rail read, so the sub-nav's active row and the stage's content cannot drift.
  const { pathname } = useLocation();
  const tab: Tab = TAB_FOR_ROW[resolveSubject(pathname).key] ?? "overview";
  const [showEdit, setShowEdit] = useState(false);
  const [showRestrict, setShowRestrict] = useState(false);
  const restriction = useMatterRestriction(id);
  const [busy, setBusy] = useState(false);

  const e = detail.data?.entity as (EntityRecord & Record<string, unknown>) | undefined;
  const statusVal = (e?.status as string | undefined) ?? "open";
  const governed = governanceFor("matter", statusVal);

  async function lifecycle(verb: "close_matter" | "reopen_matter") {
    if (!id) return;
    setBusy(true);
    try {
      const r = await mcpClient.toolsCall(verb, { matter_id: id });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? `${verb} failed`);
      showToast(verb === "close_matter" ? "Matter closed" : "Matter reopened", "success");
      void detail.refetch();
    } catch (err) {
      showToast((err as Error).message, "error");
    } finally { setBusy(false); }
  }

  if (type.isLoading || detail.isLoading) {
    return (
      <div className="screen">
        {/* №53.3.1 S0 — the skeleton follows the shape it is standing in for now
            (`.rechead` + `.lead`), not the retired `.matterhead`/`.threeb`. */}
        <div className="rechead"><div className="skeleton sk-line" style={{ width: "38%", height: 22 }} /></div>
        <div className="skeleton sk-line" style={{ width: "55%", marginBottom: 14 }} />
      </div>
    );
  }
  if (detail.isError || !detail.data || !type.data || !e) {
    return (
      <div className="screen"><div className="error-state">
        <h3>Couldn’t load this matter</h3>
        <p>The trust balance is read live from the ledger and WIP is derived — rather than show stale figures, none are shown.</p>
        <button data-variant="primary" onClick={() => detail.refetch()}>Retry</button>
      </div></div>
    );
  }

  const d = detail.data;
  const title = (e.title as string | undefined) ?? e.id;
  // №48 — `client` / `practice_area` were read ONLY to build the caption line,
  // and went with it when the caption was deleted. SUPERSEDED 53.3.1 S0
  // (D-CR26 decision item 1): the `.rechead` lead line revives exactly these
  // two, further down — this time resolved through `fieldDisplay` +
  // `forward_refs` rather than printed raw, which is the fix №48's own
  // comment (below, git blame) named but deferred.

  const tasks = d.back_refs?.tasks ?? [];
  // №22.1 (D-CC9) — an escalated agent step on THIS matter renders as the compact
  // decision card in the DO-NEXT rail (the fourth home of the ONE card, D-CC4). Only
  // OPEN tasks carry a live card — a done/dropped task's standing reason must not show
  // (back-refs, unlike list_needs_you, don't filter by status).
  const isActive = (t: { status: string }) => t.status !== "done" && t.status !== "dropped";
  const needsYouTasks = tasks.filter((t) => (t.needs_you_reason ?? "").trim().length > 0 && isActive(t));

  // №22.2 (D-CV1) — the receipt: cards answered here resolve in place, and linger as a
  // one-line receipt until the matter's next visit (they don't disappear on the spot).
  const nyIds = new Set(needsYouTasks.map((t) => t.id));
  const answeredExtraMatter = Object.entries(ja.map).filter(([tid]) => !nyIds.has(tid));
  const chooseAndRecord = (t: (typeof needsYouTasks)[number], optionIndex: number) => {
    const opt = t.needs_you_options?.[optionIndex];
    // №22.3 (D-CH2) — focus the panel on the run as it lands; the page doesn't move.
    coPanel.notifyAnswered({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name });
    answer.mutate(
      { taskId: t.id, optionIndex, ...(opt?.label ? { optionLabel: opt.label } : {}) },
      {
        onSuccess: () =>
          ja.record(t.id, {
            optionIndex, label: opt?.label ?? "your choice", at: new Date().toISOString(), outcome: "executed",
            reason: t.needs_you_reason ?? "", options: t.needs_you_options ?? [], evidence: t.needs_you_evidence ?? [],
            agentName: t.agent_name,
            // №22.5 (F1) — the receipt belongs to THIS question, not to the task.
            ...(t.needs_you_batch ? { batchIndex: t.needs_you_batch.index } : {}),
          }),
      },
    );
  };

  /** №22.5 (F10) — answer in your own words, from the matter rail. */
  const replyToCard = (t: (typeof needsYouTasks)[number], text: string) => {
    coPanel.focusTask({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name });
    if (t.agent_id) play.mutate({ agentId: t.agent_id, taskId: t.id, input: text, requestedByName: "you" });
  };

  /** №22.5 (C4/F1) — skip leaves the same kind of receipt, and advances a batch. */
  const skipAndRecord = (t: (typeof needsYouTasks)[number]) => {
    coPanel.notifyAnswered({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name });
    skip.mutate({ taskId: t.id }, {
      onSuccess: (res) => ja.record(t.id, {
        optionIndex: -1, label: "Skipped", at: new Date().toISOString(), outcome: "skipped",
        reason: t.needs_you_reason ?? "", options: t.needs_you_options ?? [], evidence: t.needs_you_evidence ?? [],
        agentName: t.agent_name,
        ...(t.needs_you_batch ? { batchIndex: t.needs_you_batch.index } : {}),
        ...(res?.consequence ? { consequence: res.consequence } : {}),
      }),
    });
  };



  const arrRows = arrangements.data ?? [];
  const byArr = (wip.data?.by_arrangement ?? {}) as Record<string, number>;

  const trustAccounts = trust.data?.accounts ?? [];

  // №23 Stage C (part 2), David's ruling 15 Aug — the recordcap crumb badge:
  // the single most urgent declared date, elevated so the two-stage window
  // (D-MS2 rev) is visible without scrolling to the Key Dates section below.
  // Same `keyDateState()` the section itself uses (`key-dates.ts`) — "most
  // urgent date wins, nothing renders when no date is in a window." A date
  // that's merely informational (no `attention_days`/`urgent_days`) or has
  // already passed is never a candidate — this badge is for something that
  // needs attention now, not the whole registry (that's the table below).
  const capCols = columnsByName(type.data);
  const mostUrgent = mostUrgentKeyDate(
    type.data.fields
      .filter((f) => (f.type === "date" || f.type === "datetime") && (f.attention_days != null || f.urgent_days != null))
      .map((f) => ({ name: f.name, value: (e as Record<string, unknown>)[f.name], attentionDays: f.attention_days, urgentDays: f.urgent_days })),
  );
  const mostUrgentField = mostUrgent ? type.data.fields.find((f) => f.name === mostUrgent.name) : undefined;
  const mostUrgentWhen =
    mostUrgent && mostUrgentField
      ? fieldDisplay(mostUrgentField, (e as Record<string, unknown>)[mostUrgent.name], undefined, capCols) || ""
      : "";
  // X-3 (ride-along, Stage C3) — the locked reference (`docs/23-matter-options/`,
  // `d-full-{theme}.png`) reads "Settlement 5 Aug", not "Settlement date Aug 18":
  // the value alongside the label already says it's a date, so the field-name
  // suffix is redundant on a `*_date` field. Scoped to this one badge — the Key
  // Dates table's row label keeps the field name verbatim.
  const mostUrgentLabel = mostUrgent ? sentenceLabel(mostUrgent.name).replace(/ date$/i, "") : "";
  // №53.3.1 S0 (D-CR24/D-CR26) — the standalone `.recordcap` urgency badge
  // this comment used to describe is RETIRED: the drawing's landing view is
  // rechead · lead · tabs · stream+facts and nothing else (decision item 1),
  // and VISION-1 draws no crumb badge above the title. The one fact it carried
  // — the record's nearest declared key date — survives as the facts rail's
  // "Next key date" row (`matter-stream-model.ts`'s `factsFor`), plain text
  // with no urgency-tone suffix: the facts rail is never coloured (D-CR26 —
  // `needs_person` on a Surfaces claim is the ONLY red on the page). This is a
  // stated deviation from the literal eviction list (which named `threeb` and
  // the DOES card, not this badge) — flagged rather than silently absorbed;
  // see the slice's own report.
  const nextKeyDate = mostUrgent && mostUrgentField ? `${mostUrgentLabel} ${mostUrgentWhen}`.trim() : null;
  // №48 — the caption line is GONE. David, 2026-08-17: *"rendering the customer
  // line on the matter page is trash."* He is right, and the render is why:
  // it read `C-0001 · Property · Acceptance Owner Runner` — a RAW CONTACT ID
  // where a name belongs, next to two values the page already shows better.
  // 53.3.1 S0 revives a lead line (D-CR26 decision item 1: "practice area · fee
  // basis · client name · carried by <hire>") but the id-resolution defect
  // this comment names is fixed THIS time: `client` is read through
  // `fieldDisplay` + `forward_refs`, the same resolver the declared sections
  // use, so it is a name, never a raw `C-0001`.
  const practiceAreaField = type.data.fields.find((f) => f.name === "practice_area");
  const practiceArea = practiceAreaField ? fieldDisplay(practiceAreaField, (e as Record<string, unknown>).practice_area, d.forward_refs, capCols) : "";
  const clientField = type.data.fields.find((f) => f.name === "client");
  const clientName = clientField ? fieldDisplay(clientField, (e as Record<string, unknown>).client, d.forward_refs, capCols) : "";
  // `fee_basis` / `carried_by` / `supervising_principal` are read the same way
  // the pre-53.3.1 facts rail already read them — as loose, undeclared string
  // probes. AUDIT FINDING (53.3.1 S0): none of the three is a declared field on
  // ANY entity type in this repo (grepped `src/` clean) — so on every real
  // matter today these reads are `undefined` and these two lead-line parts,
  // like the old facts-rail rows they came from, have never actually rendered
  // in production. D-CR2 handles the absence honestly (the part just drops),
  // but the drawing's "time & materials · carried by the Matter Runner" is
  // currently unreachable data, not a rendering bug — worth a point release
  // that either declares these fields for real or sources them from where the
  // domain actually keeps them (fee basis from the fee arrangement's
  // `billing_method`; carried-by from the matter's team lead).
  const feeBasis = typeof (e as Record<string, unknown>).fee_basis === "string" ? ((e as Record<string, unknown>).fee_basis as string) : "";
  const carriedBy = typeof (e as Record<string, unknown>).carried_by === "string" ? ((e as Record<string, unknown>).carried_by as string) : "";
  const decides = typeof (e as Record<string, unknown>).supervising_principal === "string" ? ((e as Record<string, unknown>).supervising_principal as string) : "";
  const leadLine = [practiceArea, feeBasis, clientName, carriedBy ? `carried by ${carriedBy}` : ""]
    .filter((part) => part.trim().length > 0)
    .join(" · ");

  // №53.3.1 S0 (decision item 5) — the ONE read `_meta._related` replaces the
  // five per-hook count sources the deleted tab bar (then the nav, then
  // `MatterSubViewCounts` below) used to feed. The hooks themselves are
  // UNCHANGED and still back their own tabs' bodies (`arrangements.data`,
  // `parties.data`, …) — only the count PLUMBING moves.
  const related = e._meta?._related;
  const relatedCounts = relatedCountsByRowKey(related);
  const facSurfaces = surfaceRows(related, "matter", id!);

  return (
    <div className="screen">
      {/* №53.3.1 S0 (D-CR24/D-CR26) — THE .rechead HEADER. Replaces the portalled
          stage-head declaration (`MatterStageHead`/`<StageActions>`, both
          retired — see the report for what stays shell-owned vs page-owned):
          serif title · status pill · right-aligned lifecycle verbs, one flex
          row, per the vision's H1. The Restrict control moves INTO this group
          (it used to be the crumb's fourth verb); it is not drawn in
          VISION-1's own screenshot (the demo matter is unrestricted), so its
          exact position among the three is this rung's own call, not a
          transcription. */}
      <div className="rechead" data-testid="rechead">
        <h2>{title}</h2>
        <span className={`pill pill--${statusVal}`} data-testid="rechead-status">{statusVal}</span>
        {restriction.data?.restriction?.access === "restricted" && (
          <span className="badge badge--oob" data-testid="rechead-restricted">Restricted</span>
        )}
        <div className="recacts">
          {governed ? (
            <button disabled={busy} onClick={() => void lifecycle("reopen_matter")}>Reopen matter</button>
          ) : (
            <>
              <button onClick={() => setShowEdit(true)}>Edit</button>
              <button disabled={busy} onClick={() => void lifecycle("close_matter")}>Close matter</button>
            </>
          )}
          {/* №11.2.4 — restrict from the record; available even when governed/closed. */}
          <button onClick={() => setShowRestrict(true)}>
            {restriction.data?.restriction?.access === "restricted" ? "Manage access" : "Restrict"}
          </button>
        </div>
      </div>
      {/* D-CR26 decision item 1 — practice area · fee basis · client · carried
          by, each part dropping when absent (D-CR2). See the const above for
          why fee basis / carried-by are currently always-absent on real data. */}
      {leadLine && <p className="lead" data-testid="rechead-lead">{leadLine}</p>}

      <CreateEntityModal entityType={type.data} existing={e} open={showEdit} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); void detail.refetch(); }} />
      <RestrictMatterModal open={showRestrict} onClose={() => setShowRestrict(false)} matterId={id!} matterName={title} />

      {/* №53.3.1 S0 (D-CR24, decision item 1 — "nothing else") — EVICTED from the
          landing view: the `threeb` money strip (D-MS9's header-strip position,
          itself already the SECOND reversal of the money question) and the DOES
          actions card. This is the THIRD position, said out loud in the spec:
          money moves into the Billing/Trust Surfaces rows' claim lines below
          (server-composed — `related-claims.ts`), and the DOES card has no
          relocation — the vision draws no full unrestricted action list on the
          landing view; `ContextActionBar` stays mounted, scoped, inside every
          tab that already had one (Billing/Trust/Parties/Register/Conflicts).
          A verb reachable ONLY from the old unscoped DOES card (none currently
          declared as such) would become unreachable from this page — flagged
          in the report, not discovered here. `GovernedNotice`'s explanatory
          copy ("this matter is closed — its details are frozen") is dropped
          with it; the functional affordance survives — the status pill above
          reads "closed" and the header's own button already swaps to "Reopen
          matter" when governed. */}

      {/* №29 (S4, §3 ④) — the in-page tab bar is DELETED. The record's views are tabs
          (№48 reverted the rail-based nav) whose counts are declared here — now
          sourced from `_meta._related` (decision item 5) instead of five per-hook
          reads. Trust/Process carry no badge, matching both the shipped tab bar's
          own prior behaviour and VISION-1's own tab strip (neither draws a count
          on Trust, Process, Program or Details). */}
      <MatterSubViewCounts
        parties={relatedCounts["place-record-parties"]}
        billing={relatedCounts["place-record-billing"]}
        register={relatedCounts["place-record-register"]}
        conflicts={relatedCounts["place-record-conflicts"]}
        files={relatedCounts["place-record-files"]}
      />

      {tab === "overview" && (
        <>
          {/* №53 · 53.3 — THE STREAM TAKES THE LANDING SLOT.
              What was here — an Activity list, a Work panel and a What-we-know
              card in a two-column `.recordgrid` — is the Overview COMPOSITE the
              spec replaces. It answered "what is attached to this matter"; the
              stream answers "what is happening on it", which is the question a
              partner opens the page with. The decision card is not gone: it sits
              IN the stream, at the moment it was raised, which is where it
              happened (`docs/53-mockups/matters-1.png`).
              Net zero on the tab budget — the `Record` tab is now `Stream`. */}
          <MatterStream
            events={stream.data?.events ?? []}
            todayKey={new Date().toISOString().slice(0, 10)}
            loading={stream.isLoading}
            facts={{
              entityId: id!,
              entityType: "matter",
              feeBasis,
              carriedBy,
              decides,
              nextKeyDate,
            }}
            surfaces={facSurfaces}
            /* D-CC4 / F10 / D-CH2 — the matter is one of FOUR card homes, and the
               card renders IN the stream at the moment it was raised. The stream's
               own summary line steps aside for it. */
            renderCard={(ev) => {
              const t = needsYouTasks.find((x) => x.id === ev.taskId);
              if (!t) return null;
              const isThis = answer.variables?.taskId === t.id;
              const r = receiptFor(ja.map[t.id], t); // №22.5 (F1)
              return (
                <DecisionCard
                  variant={r ? "full" : "compact"}
                  reason={t.needs_you_reason ?? ""}
                  evidence={t.needs_you_evidence}
                  options={t.needs_you_options}
                  context={{ agentName: t.agent_name }}
                  resolved={r ? { optionIndex: r.optionIndex, label: r.label, at: r.at, outcome: r.outcome } : null}
                  onChoose={r ? undefined : (optionIndex) => chooseAndRecord(t, optionIndex)}
                  choosing={answer.isPending && isThis}
                  chosenIndex={answer.isPending && isThis ? (answer.variables?.optionIndex ?? null) : null}
                  batch={t.needs_you_batch ?? null}
                  provenance={t.provenance ?? null}
                  skipConsequence={t.needs_you_skip_consequence ?? null}
                  skipped={t.needs_you_skipped}
                  onSkip={r ? undefined : () => skipAndRecord(t)}
                  onReply={r ? undefined : (text) => replyToCard(t, text)}
                  replying={play.isPending && play.variables?.taskId === t.id}
                  skipping={skip.isPending && skip.variables?.taskId === t.id}
                  openThreadHref={`/app/task/${t.id}`}
                  onOpenThread={() => coPanel.focusTask({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name })}
                  error={answer.isError && isThis ? ((answer.error as Error)?.message ?? "That option couldn’t be completed.") : null}
                />
              );
            }}
          />
        </>
      )}

      {/* №53.3.1 S0 (E-33, decision item 3) — DETAILS. The record's declared
          sections (RecordView — Key dates, Team, Effective rates), the
          "answered elsewhere" receipts and "Calls made on this matter" all
          evicted from the landing view — confirmed (audit) that none of the
          three already renders in the stream's own day groups, so all three
          needed a destination rather than simply dropping. Ordered: declared
          sections first (what the record IS), then the two receipt histories
          (what happened to it) — an assumption, flagged: the spec named WHERE
          each evicted block goes but not their relative order once landed
          together on one tab. */}
      {tab === "details" && (
        <>
          {/* №23 Stage C (part 2), D-MS9 — RecordView renders FULL STAGE WIDTH,
              outside `.recordgrid`'s narrow `1fr` (~440px) column. This is the
              width fix D-MS9 exists because of: the rate ladder's SOURCE
              attribution ("Client rate · RATE-0004 (Acme Holdings Pty Ltd)")
              fits on one line at full width and wraps in that narrow column —
              a ladder whose source doesn't fit fails its own purpose. RecordView
              keeps the fix now that its declared sections (Key Dates, Team,
              Effective Rates) are real data grids, not a handful of short
              fields — only its PLACE on the record moved (E-33), not this. */}
          <RecordView
            entityType={type.data}
            entity={e}
            forwardRefs={d.forward_refs}
            viewConfig={views.recordView}
            rateLadder={effectiveRates.data}
            team={team.data}
          />

          {/* Cards ANSWERED elsewhere but belonging to this matter — the receipt
              still belongs on the record (D-CV1: answering is a receipt, never a
              disappearance). Confirmed this rung: the stream's own day groups
              carry no equivalent (buildAnchorStream's card kind is OPEN
              decisions only — collaboration.ts:1018 — there is no "answered"
              event kind synthesized into it), so this stays reachable here
              rather than silently losing the receipt. */}
          {answeredExtraMatter.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {answeredExtraMatter.map(([tid, r]: [string, AnsweredReceipt]) => (
                <div key={tid} style={{ marginBottom: 8 }}>
                  <DecisionCard
                    variant="full"
                    reason={r.reason}
                    options={r.options}
                    context={{ agentName: r.agentName }}
                    resolved={{ optionIndex: r.optionIndex, label: r.label, at: r.at, outcome: r.outcome }}
                    openThreadHref={`/app/task/${tid}`}
                    onOpenThread={() => coPanel.focusTask({ taskId: tid, agentName: r.agentName, label: r.anchorLabel })}
                  />
                </div>
              ))}
            </div>
          )}

          {/* №22.6 (H2 / O1) — EVERY CALL MADE ON THIS MATTER.
              The spec's own walk: "show me every call made on this matter". The
              thread held a receipt per task and the inbox's receipt is page-local
              by design (D-CV1), so the record — the place a firm actually asks the
              question from, and the place a regulator would — held nothing at all.
              Same component as the home's "Answered today"; two implementations of
              a history are two histories.
              `hideWhenEmpty` is OFF here: "no calls have been made on this matter"
              is a real answer to the question being asked, and an honest empty
              state beats a section that silently is not there. Moved beneath the
              declared sections this rung (53.3.1 S0, decision item 2) — its
              receipts remain reachable; killing the section outright is not this
              rung's call (spec §2). */}
          <AnsweredStrip
            heading="Calls made on this matter"
            note="who chose, what, when — and the evidence that was on the card"
            anchorType="matter"
            anchorNo={id}
            limit={100}
          />
        </>
      )}

      {tab === "billing" && (
        <div className="vt">
          <div className="card card--accent">
            <div className="card__h">Work in progress <span className="sub">get_wip_for_matter · derived, uncapped</span><span className="spacer" />
              <ContextActionBar context="matter" id={id!} only={["prepare_invoice"]} onDone={() => void wip.refetch()} />
            </div>
            <div className="card__b"><div className="threeb">
              <div className="bal bal--wip"><span className="k">Unbilled WIP</span><span className="v money">{wip.data ? money(wip.data.wip_minor) : "—"}</span><span className="src">{wip.data ? `Σ ${wip.data.entry_count} time + disbursements` : ""}</span></div>
              <div className="bal"><span className="k">Time</span><span className="v money">{wip.data ? money(wip.data.time_minor) : "—"}</span><span className="src">across {arrRows.length} arrangement{arrRows.length === 1 ? "" : "s"}</span></div>
              <div className="bal"><span className="k">Disbursements</span><span className="v money">{wip.data ? money(wip.data.disbursement_minor) : "—"}</span><span className="src">recoverable</span></div>
            </div></div>
          </div>

          <div className="card">
            <div className="card__h">Fee arrangements <span className="sub">a matter with several treatments is the designed-for case</span><span className="spacer" />
              <ContextActionBar context="matter" id={id!} only={["create_matter_fee_arrangement", "apply_fee_package"]} onDone={() => void arrangements.refetch()} />
            </div>
            <div className="card__b">
              {arrRows.length === 0 ? <div className="muted tiny">No fee arrangements yet — add one, or apply a fee package.</div> : (
                <div className="arrs">
                  {arrRows.map((a) => {
                    const mb = methodBadge(a.billing_method);
                    const actual = byArr[a.id as string] ?? 0;
                    return (
                      <Link key={a.id} to={`/app/place/fee-arrangement/${a.id}`} className="arr">
                        <div className="arr__h"><span className="nm">{(a.description as string) ?? a.id}</span><span className={`badge ${mb.cls}`}>{mb.label}</span><span className="spacer" /><span className="badge badge--soft">{(a.status as string) ?? "open"}</span></div>
                        <div className="meta">{a.fee_package_no ? `From package ${a.fee_package_no}` : a.is_default === "yes" ? "Default arrangement" : "Fee arrangement"}</div>
                        <div className="figs"><span className="money">{money(actual)}</span><span className="muted">unbilled WIP</span></div>
                        <span className="drill">Lines · budget versions · activity →</span>
                      </Link>
                    );
                  })}
                </div>
              )}
              <div className="sub-note">The <b>arrangement picker</b> in each capture form is the same component used here at billing — one open → auto-attributed; many → pick.</div>
            </div>
          </div>
        </div>
      )}

      {tab === "trust" && (
        <div className="vt">
          <div className="card card--trust">
            <div className="card__h">Trust position <span className="sub">get_matter_trust_balance · ledger-live, never stored (G2/L-2)</span></div>
            <div className="card__b">
              {trustAccounts.length === 0 ? (
                <div className="stack">
                  <div className="muted tiny">No trust account is linked to this matter yet.</div>
                  <ContextActionBar context="matter" id={id!} homeModule="legal" only={["ensure_matter_trust_account"]} onDone={() => void trust.refetch()} />
                </div>
              ) : (
                // №13.4.5 (D-TRU2) — matter-scoped slice: this matter's statement +
                // the four actions (13.4.4) prefilled+locked to its account, incl.
                // Transfer between matters (13.4.6). Firm-wide Trust place unchanged
                // for firm-level ops; matter context never dumps into it.
                <MatterTrustSlice matterId={id!} trust={trust.data!} onRefetch={() => void trust.refetch()} />
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "parties" && (
        <div className="vt">
          <div className="card card--flush wide">
            <div className="card__h">Parties <span className="sub">the related-as join · one-or-many clients + other roles</span><span className="spacer" />
              <ContextActionBar context="matter" id={id!} homeModule="legal" placement="related:matter-party" only={["add_matter_party"]} onDone={() => void parties.refetch()} />
            </div>
            {(parties.data ?? []).length === 0 ? <div className="card__b muted tiny">No parties on this matter yet. Add the client (and any other side) above.</div> : (
              <table className="ledger-table"><thead><tr><th>Contact</th><th>Related as</th><th>Primary</th><th /></tr></thead><tbody>
                {parties.data!.map((p) => (
                  <tr key={p.id}>
                    <td className="primary" onClick={() => p.contact && navigate(`/app/place/contact/${p.contact}`)} style={{ cursor: p.contact ? "pointer" : "default" }}>{p.contact_name ?? p.contact ?? "—"}<span className="muted tiny"> · {p.contact_kind ?? ""}</span></td>
                    <td><span className={`badge ${p.role === "client" ? "badge--fixed" : "badge--soft"}`}>{(p.role ?? "").replace(/_/g, " ")}</span></td>
                    <td>{p.role === "client" ? (p.is_primary === "yes" ? <span className="badge badge--tm">Primary client</span> : (
                      <button className="linkish" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--kuvio-module-finance)", fontSize: 11 }}
                        disabled={setPrimary.isPending}
                        onClick={() => p.contact && setPrimary.mutate({ matter_id: id!, contact_id: p.contact }, { onSuccess: () => void parties.refetch() })}
                      >Make primary</button>
                    )) : "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <button className="linkish" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--color-text-tertiary)", fontSize: 11 }}
                        disabled={removeParty.isPending}
                        onClick={() => { if (confirm("Remove this party from the matter?")) removeParty.mutate({ matter_party_id: p.id }, { onSuccess: () => void parties.refetch() }); }}
                      >Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            )}
          </div>
        </div>
      )}

      {tab === "register" && (
        <div className="vt">
          <div className="card card--flush wide">
            <div className="card__h">Register of Powers &amp; Estates <span className="sub">R13 · renderer reads these rows</span><span className="spacer" />
              <ContextActionBar context="matter" id={id!} placement="related:register-entry" only={["create_register_entry"]} onDone={() => void registers.refetch()} />
            </div>
            {(registers.data ?? []).length === 0 ? <div className="card__b muted tiny">No register entries on this matter.</div> : (
              <table className="ledger-table"><thead><tr><th>Donor / deceased</th><th>Kind</th><th>Date of power</th><th>Lawyer</th></tr></thead><tbody>
                {registers.data!.map((r) => (
                  <tr key={r.id}><td>{(r.donor_name as string) ?? "—"}</td><td><span className="badge badge--soft">{(r.kind as string) ?? ""}</span></td><td className="row-date">{(r.date_of_power as string) ?? "—"}</td><td className="muted">{(r.responsible_lawyer as string) ?? "—"}</td></tr>
                ))}
              </tbody></table>
            )}
          </div>
        </div>
      )}

      {tab === "conflicts" && (
        <div className="vt">
          <div className="card card--flush wide">
            <div className="card__h">Conflict checks <span className="sub">evidence · r 128 trail</span><span className="spacer" />
              <ContextActionBar context="matter" id={id!} homeModule="legal" only={["run_conflict_check"]} onDone={() => void conflicts.refetch()} />
            </div>
            {(conflicts.data ?? []).length === 0 ? <div className="card__b muted tiny">No conflict checks recorded for this matter.</div> : (
              <table className="ledger-table"><thead><tr><th>Searched</th><th>Result</th><th>By · when</th></tr></thead><tbody>
                {conflicts.data!.map((c) => {
                  const disp = conflictResultDisplay(c.result as string | undefined);
                  return (
                    <tr key={c.id}>
                      <td>{(c.subject_names as string) ?? "—"}</td>
                      <td>
                        <Link className="sec" to={`/app/place/conflict-check/${c.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                          <span className={`pill ${disp.pill}`}><span className="dot" />{disp.label}</span>
                          <span>View result →</span>
                        </Link>
                      </td>
                      <td className="muted">{(c.checked_by as string) ?? "—"} · {formatRelativeTime((c.checked_at as string) ?? "")}</td>
                    </tr>
                  );
                })}
              </tbody></table>
            )}
          </div>
        </div>
      )}

      {tab === "files" && (
        <div className="vt">
          <div className="card"><div className="card__h">Files <span className="sub">thin until documents (№17/№18)</span></div>
            <div className="card__b">
              {(files.data?.files ?? []).length === 0 ? <div className="muted tiny">No files on this matter yet.</div> : (
                <div className="stack">{files.data!.files.map((f) => <div className="dotline" key={f.id}><span>{f.filename ?? f.id}</span><span className="muted tiny">{f.mime_type ?? ""}</span></div>)}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * №29 (S4, §3 ④) — the counts the deleted tab bar carried, declared for the tab
 * strip (`RecordTabs.tsx`, reverted from the rail to in-page tabs №48). Its own
 * component so the page's hook order is untouched by a declaration that lives
 * below its early returns — the same reason `MatterStageHead` (retired
 * 53.3.1 S0, its title/status/verbs now render straight into `.rechead` rather
 * than portalling into the shell's stage head) used to be split out too.
 *
 * An UNDEFINED count renders no badge, and that is not the same as zero — "Files" with no
 * badge means the count has not arrived, "Files 0" means the matter has none. The tab bar
 * drew the same distinction (`count != null`) and it survives the move.
 */
function MatterSubViewCounts({
  parties, billing, register, conflicts, files,
}: { parties?: number; billing?: number; register?: number; conflicts?: number; files?: number }) {
  useSubViewCounts({
    "place-record-parties": parties,
    "place-record-billing": billing,
    "place-record-register": register,
    "place-record-conflicts": conflicts,
    "place-record-files": files,
  });
  return null;
}
